import React, { useEffect, useState } from "react";
import { getBranches, addBranch, updateBranch, deleteBranch } from "../../Api/Api";
import { FaEdit, FaPlus } from "react-icons/fa";
import Pagination from "../Pagination/Pagination";
import BranchModal from "./BranchModal";
import { useNotification } from "../Notification/NotificationContext";

function BranchList() {
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = 10;
    const { showNotification } = useNotification();

    useEffect(() => {
        fetchBranches();
    }, [currentPage]);

    const fetchBranches = () => {
        setLoading(true);
        getBranches(currentPage, pageSize)
            .then((response) => {
                setBranches(response.data.content);
                setTotalPages(response.data.totalPages);
                setTotalCount(response.data.totalElements);
                setLoading(false);
            })
            .catch((err) => {
                showNotification(err.message, "error");
                setLoading(false);
            });
    };

    const handleShowModal = (branch = null) => {
        setSelectedBranch(branch);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setSelectedBranch(null);
        setShowModal(false);
    };

    const handleSaveBranch = async (branchData) => {
        try {
            if (selectedBranch) {
                await updateBranch(selectedBranch.id, branchData);
                showNotification("Branch updated successfully", "success");
            } else {
                await addBranch(branchData);
                showNotification("Branch added successfully", "success");
            }
            handleCloseModal();
            fetchBranches();
        } catch (error) {
            showNotification(error.message, "error");
        }
    };

    const handleDeleteBranch = async (id) => {
        if (window.confirm("Are you sure you want to delete this branch?")) {
            try {
                await deleteBranch(id);
                showNotification("Branch deleted successfully", "success");
                fetchBranches();
            } catch (error) {
                showNotification(error.message, "error");
            }
        }
    };

    if (loading) {
        return <div className="flex justify-center items-center py-6 text-gray-600">Loading...</div>;
    }

    return (
        <div className="mt-6 px-4 sm:px-6 lg:px-8">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <div className="flex items-center justify-between bg-teal-500 text-white px-6 py-4 rounded-t-xl">
                    <h2 className="text-lg font-semibold">Branches</h2>
                    <button
                        onClick={() => handleShowModal()}
                        className="inline-flex items-center px-4 py-2 bg-white text-teal-600 border border-teal-500 rounded-md hover:bg-gray-100 transition"
                    >
                        <FaPlus className="mr-2" /> Add Branch
                    </button>
                </div>

                <div className="p-4 overflow-x-auto">
                    <table className="min-w-full table-auto text-sm">
                        <thead className="bg-gray-100 text-gray-700 uppercase text-xs tracking-wider">
                        <tr>
                            <th className="p-3 text-center">ID</th>
                            <th className="p-3 text-left">Name</th>
                            <th className="p-3 text-left">Address</th>
                            <th className="p-3 text-left">City</th>
                            <th className="p-3 text-left">Actions</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                        {branches.map((branch) => (
                            <tr key={branch.id} className="hover:bg-gray-50">
                                <td className="p-3 text-center text-gray-700">{branch.id}</td>
                                <td className="p-3 text-blue-600 hover:underline">{branch.name}</td>
                                <td className="p-3">{branch.address}</td>
                                <td className="p-3">{branch.city}</td>
                                <td className="p-3 flex space-x-2">
                                    <button
                                        onClick={() => handleShowModal(branch)}
                                        className="inline-flex items-center px-3 py-1 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition"
                                    >
                                        <FaEdit className="mr-2" /> Edit
                                    </button>
                                    <button
                                        onClick={() => handleDeleteBranch(branch.id)}
                                        className="inline-flex items-center px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 transition"
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>

                    <div className="mt-4">
                        <Pagination
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            currentPage={currentPage}
                            totalCount={totalCount}
                        />
                    </div>
                </div>
            </div>

            {/* Branch Modal */}
            <BranchModal
                show={showModal}
                handleClose={handleCloseModal}
                branch={selectedBranch}
                handleSave={handleSaveBranch}
            />
        </div>
    );
}

export default BranchList;